import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const inputVariants = cva(
  "w-full px-4 py-2 border rounded-lg text-sm transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-gray-400 outline-none",
  {
    variants: {
      variant: {
        default:
          "border-gray-300 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-transparent",
        error:
          "border-red-500 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:border-transparent bg-red-50",
        success:
          "border-green-500 focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:border-transparent",
      },
      inputSize: {
        default: "h-10",
        sm: "h-8 px-3 py-1.5 text-xs",
        lg: "h-12 px-5 py-3",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  }
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  /** Right addon for units, currency, or suffixes (e.g., "UAH", "%", "kg") */
  rightAddon?: React.ReactNode
  containerClassName?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant,
      inputSize,
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      rightAddon,
      containerClassName,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId()
    const inputId = id || generatedId
    const errorId = error ? `${inputId}-error` : undefined
    const hintId = hint ? `${inputId}-hint` : undefined

    // Auto-detect error variant if error message is provided
    const resolvedVariant = error ? "error" : variant

    return (
      <div className={cn("w-full", containerClassName)}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              {leftIcon}
            </div>
          )}
          <input
            id={inputId}
            ref={ref}
            className={cn(
              inputVariants({ variant: resolvedVariant, inputSize }),
              leftIcon && "pl-10",
              (rightIcon || rightAddon) && "pr-10",
              className
            )}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={
              [errorId, hintId].filter(Boolean).join(" ") || undefined
            }
            {...props}
          />
          {rightAddon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium pointer-events-none">
              {rightAddon}
            </div>
          )}
          {rightIcon && !rightAddon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p id={errorId} className="mt-1 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={hintId} className="mt-1 text-sm text-gray-500">
            {hint}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = "Input"

export { Input, inputVariants }
